import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CalendarRange, Lock, MapPin, Pencil, Plus, Tent, Trash2, Users } from 'lucide-react';
import {
  Alert, Button, Card, Checkbox, ConfirmDialog, EmptyState, ErrorState, Field, IconButton, Input, SearchSelect,
  StatusBadge, TextSkeleton, type Tone, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { formatDate } from '../../../lib/format';
import { VenueSections } from '../components/VenueSections';

interface Step { station: string; label: string; fixed: boolean; tests: Array<{ code: string; name: string }> }
interface Plan { id: string; code: string; name: string; version: number; category: string | null; testCount: number; steps: Step[] }
interface Camp {
  id: string; name: string; startsOn: string; endsOn: string;
  status: 'RUNNING' | 'UPCOMING' | 'FINISHED';
  location: { id: string; name: string } | null;
  plan: { id: string; code: string; name: string; testCount: number };
  stepOrder: string[];
  steps: Step[];
}
interface Branch { id: string; name: string; status?: string }
interface Assignments {
  campId: string;
  /** False until the first person is assigned: until then every counter login may save anywhere. */
  enforced: boolean;
  stations: Array<{ station: string; label: string }>;
  staff: Array<{ userId: string; fullName: string; email: string; stations: string[] }>;
}

const STATUS: Record<Camp['status'], { tone: Tone; word: string }> = {
  RUNNING: { tone: 'success', word: 'Running today' },
  UPCOMING: { tone: 'info', word: 'Upcoming' },
  FINISHED: { tone: 'neutral', word: 'Finished' },
};

/**
 * The venue's camps and the Test Plan each one runs.
 *
 * The venue admin picks a health package as the camp's Test Plan, sees the
 * steps it makes, and puts them in the order the camp is laid out. Every
 * worker checked in at the camp through the camp app gets that plan's tests,
 * and the counters show the steps in this order with the next one marked.
 * The medical history stays first and the doctor last.
 */
export function Camps() {
  const { principal, permissions } = useAuth();
  const venue = principal?.partner;
  const canEdit = holds(permissions, 'partner.update');
  const { toast } = useToast();
  const camps = useQuery<{ today: string; camps: Camp[] }>(() => api.get('/camp/camps'), []);
  const [editing, setEditing] = useState<Camp | 'new' | null>(null);
  const [removing, setRemoving] = useState<Camp | null>(null);
  const [staffing, setStaffing] = useState<Camp | null>(null);

  if (!venue) {
    return <Alert tone="danger">This screen belongs to a hospital or laboratory account. Yours is not one.</Alert>;
  }

  const rows = camps.data?.camps ?? [];
  const renderCamp = (c: Camp) => (
          <Card key={c.id} className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-h4 font-semibold text-neutral-900 break-words">{c.name}</h3>
                <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-600">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarRange className="h-4 w-4" aria-hidden />
                    {c.startsOn === c.endsOn ? formatDate(c.startsOn) : `${formatDate(c.startsOn)} – ${formatDate(c.endsOn)}`}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" aria-hidden />
                    {c.location?.name ?? 'Whole venue'}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <StatusBadge tone={STATUS[c.status].tone}>{STATUS[c.status].word}</StatusBadge>
                {canEdit && (
                  <IconButton label={`Edit ${c.name}`} size="sm" onClick={() => setEditing(c)}>
                    <Pencil className="h-4 w-4" />
                  </IconButton>
                )}
                {canEdit && (
                  <IconButton label={`Delete ${c.name}`} size="sm" className="hover:text-danger-600" onClick={() => setRemoving(c)}>
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                )}
              </div>
            </div>

            <p className="mt-4 text-caption uppercase tracking-wide text-neutral-500">Test Plan</p>
            <p className="text-sm font-medium text-neutral-900">
              {c.plan.name} <span className="font-normal text-neutral-500">· {c.plan.testCount} tests</span>
            </p>

            {c.status !== 'FINISHED' && (
              <Button className="mt-4" variant="secondary" size="sm" icon={<Users className="h-4 w-4" />} onClick={() => setStaffing(c)}>
                Staff &amp; counters
              </Button>
            )}

            <p className="mt-4 text-caption uppercase tracking-wide text-neutral-500">Steps, in order</p>
            <ol className="mt-2 flex flex-wrap gap-2">
              {c.steps.map((s, i) => (
                <li key={s.station} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-sm text-neutral-800">
                  <span className="font-semibold text-primary-700">{i + 1}</span> {s.label}
                </li>
              ))}
            </ol>
          </Card>
  );
  return (
    <div className="space-y-4">
      <PageHeader
        title="Camps"
        hint="Pick each camp's Test Plan and the order of its steps. The camp app follows this order."
        actions={canEdit && <Button icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>New camp</Button>}
      />
      <VenueSections />

      {camps.loading && !camps.data && <Card className="p-5"><TextSkeleton lines={4} /></Card>}
      {camps.error && <ErrorState detail={camps.error} onRetry={camps.reload} />}
      {!camps.loading && !camps.error && rows.length === 0 && (
        <EmptyState
          icon={<Tent className="h-6 w-6" />}
          title="No camps yet"
          hint="Create a camp, choose its Test Plan, and arrange the steps the workers walk through."
          action={canEdit ? <Button icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>New camp</Button> : undefined}
        />
      )}

      {(['RUNNING', 'UPCOMING'] as const).map((st) => {
        const group = rows.filter((c) => c.status === st);
        if (!group.length) return null;
        return (
          <section key={st} aria-labelledby={`camps-${st}`} className="space-y-2">
            <h2 id={`camps-${st}`} className="text-h4 text-neutral-900">
              {st === 'RUNNING' ? 'Running today' : 'Upcoming'} <span className="font-normal text-neutral-500">({group.length})</span>
            </h2>
            <div className="grid gap-4 lg:grid-cols-2">{group.map(renderCamp)}</div>
          </section>
        );
      })}
      {rows.some((c) => c.status === 'FINISHED') && (
        <details className="group rounded-card border border-neutral-200 bg-white">
          <summary className="cursor-pointer px-5 py-3 text-small font-medium text-neutral-700 hover:text-neutral-900 max-md:py-3.5">
            Finished camps ({rows.filter((c) => c.status === 'FINISHED').length})
          </summary>
          <div className="grid gap-4 p-4 pt-0 lg:grid-cols-2">{rows.filter((c) => c.status === 'FINISHED').map(renderCamp)}</div>
        </details>
      )}

      {editing && (
        <CampEditor
          venueId={venue.id}
          camp={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(name, isNew) => {
            setEditing(null);
            camps.reload();
            toast('success', isNew ? `${name} is set up.` : `${name} is saved. Workers checked in from now on follow the new order.`);
          }}
        />
      )}

      {staffing && (
        <StaffDialog camp={staffing} canEdit={canEdit} onClose={() => setStaffing(null)} />
      )}

      {removing && (
        <ConfirmDialog
          title={`Delete ${removing.name}?`}
          what="The camp and its step order are removed."
          affects="Workers already checked in keep the tests they were given. Workers checked in from now on get the tests their own order was raised with."
          confirmLabel="Delete camp" tone="danger"
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await api.del(`/camp/camps/${removing.id}`);
              toast('success', `${removing.name} is deleted.`);
              setRemoving(null);
              camps.reload();
            } catch (e) {
              throw new Error(errorMessage(e));
            }
          }}
        />
      )}
    </div>
  );
}

function CampEditor({ venueId, camp, onClose, onSaved }: {
  venueId: string;
  camp: Camp | null;
  onClose: () => void;
  onSaved: (name: string, isNew: boolean) => void;
}) {
  const plans = useQuery<Plan[]>(() => api.get('/camp/plans'), []);
  const venue = useQuery<{ locations?: Branch[] }>(() => api.get(`/partners/${venueId}`), [venueId]);
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());

  const [name, setName] = useState(camp?.name ?? '');
  const [locationId, setLocationId] = useState(camp?.location?.id ?? '');
  const [startsOn, setStartsOn] = useState(camp?.startsOn ?? today);
  const [endsOn, setEndsOn] = useState(camp?.endsOn ?? today);
  const [planId, setPlanId] = useState(camp?.plan.id ?? '');
  // The middle steps, in the order the admin wants. Null = the plan's own order.
  const [order, setOrder] = useState<string[] | null>(camp ? camp.stepOrder : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plan = plans.data?.find((p) => p.id === planId) ?? null;
  const branches = (venue.data?.locations ?? []).filter((b) => b.status !== 'INACTIVE');

  // The plan's steps laid out in the chosen order: history, the middle, doctor.
  const steps = useMemo(() => {
    if (!plan) return [];
    const first = plan.steps.filter((s) => s.station === 'HISTORY');
    const last = plan.steps.filter((s) => s.station === 'PHYSICIAN');
    const middle = plan.steps.filter((s) => !s.fixed);
    const wanted = order ?? middle.map((s) => s.station);
    const sorted = [...middle].sort((a, b) => {
      const ia = wanted.indexOf(a.station); const ib = wanted.indexOf(b.station);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    return [...first, ...sorted, ...last];
  }, [plan, order]);

  const move = (station: string, by: -1 | 1) => {
    const middle = steps.filter((s) => !s.fixed).map((s) => s.station);
    const i = middle.indexOf(station);
    const j = i + by;
    if (i < 0 || j < 0 || j >= middle.length) return;
    [middle[i], middle[j]] = [middle[j]!, middle[i]!];
    setOrder(middle);
  };

  const ready = name.trim().length >= 2 && !!planId && !!startsOn && !!endsOn && endsOn >= startsOn;

  async function save() {
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(), startsOn, endsOn, packageId: planId,
      partnerLocationId: locationId || null,
      stepOrder: steps.filter((s) => !s.fixed).map((s) => s.station),
    };
    try {
      if (camp) await api.patch(`/camp/camps/${camp.id}`, body);
      else await api.post('/camp/camps', body);
      onSaved(body.name, !camp);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={camp ? `Edit ${camp.name}` : 'New camp'}
      info="Workers checked in at this camp through the camp app get this Test Plan's tests, and the counters show the steps in this order."
      onClose={onClose}
      wide
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={!ready}>{camp ? 'Save camp' : 'Create camp'}</Button>
        </div>
      )}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Camp name" required className="md:col-span-2">
          {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Tata Motors Pune — November camp" autoFocus />}
        </Field>
        <Field label="First day" required>
          {(p) => <Input {...p} type="date" value={startsOn} onChange={(e) => { setStartsOn(e.target.value); if (endsOn < e.target.value) setEndsOn(e.target.value); }} />}
        </Field>
        <Field label="Last day" required error={endsOn && startsOn && endsOn < startsOn ? 'The last day cannot be before the first day.' : null}>
          {(p) => <Input {...p} type="date" value={endsOn} min={startsOn} onChange={(e) => setEndsOn(e.target.value)} />}
        </Field>
        <Field label="Where" hint="A camp for one branch applies only to workers booked at that branch.">
          {() => (
            <SearchSelect aria-label="Where" value={locationId} onChange={setLocationId}
              placeholder="Whole venue" searchPlaceholder="Search branches…"
              options={[{ value: '', label: 'Whole venue' }, ...branches.map((b) => ({ value: b.id, label: b.name }))]} />
          )}
        </Field>
        <Field label="Test Plan" required status={plans.loading ? 'Loading plans…' : undefined}>
          {() => (
            <SearchSelect aria-label="Test Plan" value={planId} onChange={(v) => { setPlanId(v); setOrder(null); }}
              placeholder="Choose a Test Plan" searchPlaceholder="Search Test Plans…"
              options={(plans.data ?? []).map((pl) => ({ value: pl.id, label: pl.name, hint: `${pl.testCount} tests` }))} />
          )}
        </Field>
      </div>

      {plans.error && <Alert className="mt-4">{plans.error}</Alert>}
      {camp && planId !== camp.plan.id && (
        <Alert tone="warning" className="mt-4">
          Workers already checked in keep the plan they were given. The new plan applies to check-ins from now on.
        </Alert>
      )}

      {plan && (
        <section className="mt-5" aria-labelledby="steps-heading">
          <h3 id="steps-heading" className="text-sm font-semibold text-neutral-900">Steps, in the order the camp runs them</h3>
          <p className="mt-0.5 text-caption text-neutral-500">Medical history is always first and the doctor always last. Move the others to match the camp's layout.</p>
          <ol className="mt-3 space-y-2">
            {steps.map((s, i) => {
              const middle = steps.filter((x) => !x.fixed);
              const mi = middle.findIndex((x) => x.station === s.station);
              return (
                <li key={s.station} className="flex items-center gap-3 rounded-input border border-neutral-200 bg-white px-3 py-2.5 dark:bg-neutral-900">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-900">{s.label}</p>
                    {s.tests.length > 0 && (
                      <p className="truncate text-caption text-neutral-500" title={s.tests.map((t) => t.name).join(', ')}>
                        {s.tests.map((t) => t.name).join(', ')}
                      </p>
                    )}
                  </div>
                  {s.fixed ? (
                    <span className="inline-flex items-center gap-1 text-caption text-neutral-500"><Lock className="h-3.5 w-3.5" aria-hidden /> Fixed</span>
                  ) : (
                    <div className="flex shrink-0 gap-1">
                      <IconButton label={`Move ${s.label} up`} size="sm" variant="secondary" disabled={mi === 0} onClick={() => move(s.station, -1)}>
                        <ArrowUp className="h-4 w-4" />
                      </IconButton>
                      <IconButton label={`Move ${s.label} down`} size="sm" variant="secondary" disabled={mi === middle.length - 1} onClick={() => move(s.station, 1)}>
                        <ArrowDown className="h-4 w-4" />
                      </IconButton>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {error && <Alert className="mt-4">{error}</Alert>}
    </Dialog>
  );
}

/**
 * Who works which counter at one camp.
 *
 * Until anyone is assigned, every counter login may save at any counter, as
 * before. Once one person is assigned, the camp is held to the assignments:
 * a counter save by someone not assigned to that counter is refused, and the
 * camp app offers each person only their own counters.
 */
function StaffDialog({ camp, canEdit, onClose }: { camp: Camp; canEdit: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const data = useQuery<Assignments>(() => api.get(`/camp/camps/${camp.id}/assignments`), [camp.id]);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (userId: string, current: string[], station: string) => {
    const next = current.includes(station) ? current.filter((s) => s !== station) : [...current, station];
    setSaving(userId); setError(null);
    try {
      await api.put(`/camp/camps/${camp.id}/assignments/${userId}`, { stations: next });
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(null);
    }
  };

  const a = data.data;
  // Only the counters this camp's Test Plan runs, in the camp's order.
  const label = new Map((a?.stations ?? []).map((s) => [s.station, s.label]));
  const counters = camp.steps.map((s) => ({ station: s.station, label: label.get(s.station) ?? s.label }));
  return (
    <Dialog
      title={`Staff & counters — ${camp.name}`}
      info="Give each person the counters they work. The camp app then offers them only those counters, and a save at any other counter is refused."
      onClose={onClose}
      wide
      footer={<div className="flex justify-end"><Button variant="secondary" onClick={() => { if (a) toast('info', a.enforced ? 'Counter assignments are in force for this camp.' : 'No one is assigned, so every counter login can save at any counter.'); onClose(); }}>Done</Button></div>}
    >
      {data.loading && !a && <TextSkeleton lines={5} />}
      {data.error && <ErrorState detail={data.error} onRetry={data.reload} />}
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      {a && (
        <>
          <Alert tone={a.enforced ? 'info' : 'warning'} className="mb-4">
            {a.enforced
              ? 'Assignments are in force: people can save only at the counters ticked for them.'
              : 'No one is assigned yet, so any counter login can save at any counter. Tick a counter for someone to start.'}
          </Alert>
          {a.staff.length === 0 && <EmptyState icon={<Users className="h-6 w-6" />} title="No staff logins" hint="Invite your staff from Staff first." />}
          <ul className="divide-y divide-neutral-100">
            {a.staff.map((u) => (
              <li key={u.userId} className="py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium text-neutral-900 break-words">{u.fullName}</p>
                  <p className="text-sm text-neutral-500 break-all">{u.email}</p>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                  {counters.map((s) => (
                    <Checkbox
                      key={s.station}
                      label={s.label}
                      checked={u.stations.includes(s.station)}
                      disabled={!canEdit || saving === u.userId}
                      onChange={() => toggle(u.userId, u.stations, s.station)}
                    />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Dialog>
  );
}
